import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { FileText, Plus, Trash2, Loader2 } from 'lucide-react';
import { DEFAULT_TERMS } from './StandardTerms';

export default function DocumentTermsSettings() {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axiosInstance.get('/public/document-terms')
      .then((r) => setTerms(Array.isArray(r.data?.terms) ? r.data.terms : DEFAULT_TERMS))
      .catch(() => setTerms(DEFAULT_TERMS))
      .finally(() => setLoading(false));
  }, []);

  const updateTerm = (i, value) => setTerms((prev) => prev.map((t, idx) => (idx === i ? value : t)));
  const removeTerm = (i) => setTerms((prev) => prev.filter((_, idx) => idx !== i));
  const addTerm = () => setTerms((prev) => [...prev, '']);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axiosInstance.put('/settings/document-terms', { terms });
      setTerms(r.data.terms);
      toast.success('Standaardteksten opgeslagen');
    } catch {
      toast.error('Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="document-terms-settings">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <FileText className="w-6 h-6 text-[#1a5276]" /> Documentteksten
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Deze regels verschijnen standaard op elke offerte en factuur (preview, PDF en klantpagina).
        </p>
      </div>

      <Card className="bg-white shadow-sm border-gray-200">
        <CardHeader>
          <CardTitle className="text-gray-800 text-base">Standaard voorwaarden</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? (
            <p className="text-gray-500 text-sm">Laden...</p>
          ) : (
            <>
              {terms.map((t, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <span className="text-gray-400">•</span>
                  <Input
                    value={t}
                    onChange={(e) => updateTerm(i, e.target.value)}
                    placeholder="Tekst voor op offerte/factuur..."
                    className="bg-white border-gray-200"
                    data-testid={`term-input-${i}`}
                  />
                  <Button variant="ghost" size="sm" onClick={() => removeTerm(i)} className="text-red-500 hover:text-red-700 shrink-0" data-testid={`term-delete-${i}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              {terms.length === 0 && (
                <p className="text-gray-400 text-sm italic">Geen regels — het blok wordt niet getoond op documenten.</p>
              )}
              <div className="flex justify-between pt-2">
                <Button variant="outline" size="sm" onClick={addTerm} data-testid="term-add-btn">
                  <Plus className="w-4 h-4 mr-1" /> Regel toevoegen
                </Button>
                <Button onClick={save} disabled={saving} className="bg-[#1a5276] hover:bg-[#154360]" data-testid="terms-save-btn">
                  {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opslaan...</> : 'Opslaan'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white shadow-sm border-gray-200">
        <CardHeader>
          <CardTitle className="text-gray-800 text-base">Voorbeeld</CardTitle>
        </CardHeader>
        <CardContent>
          {terms.filter((t) => t.trim()).length === 0 ? (
            <p className="text-gray-400 text-sm italic">Geen tekst om te tonen</p>
          ) : (
            <div style={{ padding: '10px 14px', background: '#f8f9fa', borderLeft: '3px solid #1a5276', borderRadius: '4px', fontSize: '12px', color: '#555' }}>
              {terms.filter((t) => t.trim()).map((t, i) => (
                <p key={i} style={{ margin: '2px 0' }}>• {t}</p>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
